import React from "react";
import { ENROLLMENT_ERROR_PAGE, ENROLLMENT_STEPS_NUMBER } from "constants/enrollment";
import EligibilityCheck from "./steps/EligibilityCheck";
import EligibilityError, { APIError, EligibilityErrorUnderAge } from "./steps/partials/EligibilityError";
import EligibilitySuccess from "./steps/EligibilitySuccess";
import CarePathwayTreat from "./steps/CarePathwayTreat";
import CarePathwayPrevent from "./steps/CarePathwayPrevent";
import ProgramResult from "./steps/ProgramResult";
import CreateAccount from "./steps/CreateAccount";
import ShippingAddress from "./steps/ShippingAddress";
import EnrollmentSuccess from "./steps/EnrollmentSuccess";

/**
 * @description Renders the appropriate step component based on the current step in the enrollment process. If there's an error screen to show, it renders that instead.
 * @param {string} step - The current step in the enrollment process.
 * @param {object} formData - The form data for all steps, used to populate the fields in each step.
 * @param {string|null} errorScreen - If there's an error to show, this will be set to the appropriate error screen identifier. Otherwise, it will be null.
 * @param {function} resetToInitialStep - A function to reset the enrollment process back to the initial step. This is typically called when the user clicks "Try Again" on an error screen.
 * @param {boolean} isSubmitting - A boolean indicating whether a form submission is currently in progress. This can be used to disable buttons and show loading indicators in the step components.
 * @param {function} handleFormChange - A function to handle changes to form fields in the step components. It should accept three parameters: the step key (e.g., "eligibility", "treat", etc.), the field name, and the new value.
 * @param {function} handleCheckEligibility - A function to handle the submission of the eligibility check form.
 * @param {function} handleEligibilitySuccessNext - A function to handle the "Next" action on the eligibility success screen.
 * @param {function} handleTreatNext - A function to handle the submission of the care pathway treat form.
 * @param {function} handleInterpretationSubmit - A function to handle the submission of the care pathway prevent form.
 * @param {function} handleProgramContinue - A function to handle the "Continue" action on the program result screen.
 * @param {function} handleMigrateUser - A function to handle user migration during account creation, if applicable.
 * @param {function} handleCreateAccount - A function to handle the submission of the create account form.
 * @param {function} handleAddShippingAddress - A function to handle the submission of the shipping address form.
 * @param {function} handleVerifyShippingAddress - A function to handle the verification of the shipping address, if applicable.  
 * @returns 
 */
export default function StepRenderer({
    step,
    formData,
    errorScreen,
    resetToInitialStep,
    isSubmitting,
    handleFormChange,

    handleCheckEligibility,
    handleEligibilitySuccessNext,
    handleTreatNext,
    handleInterpretationSubmit,
    handleProgramContinue,
    handleMigrateUser,
    handleCreateAccount,
    handleAddShippingAddress,
    handleVerifyShippingAddress,
    SPONSOR_ID
}) {

    console.log("StepRenderer: step", step , formData, errorScreen, isSubmitting);
    // If there's an error screen to show, render it instead of the current step
    if (errorScreen === ENROLLMENT_ERROR_PAGE.UNDER_AGE) {
        return <EligibilityErrorUnderAge onSubmit={resetToInitialStep} />;
    }
    else if (errorScreen === ENROLLMENT_ERROR_PAGE.DEFAULT) {
        return <EligibilityError onSubmit={resetToInitialStep} />;
    }
    else if (errorScreen === ENROLLMENT_ERROR_PAGE.API_ERROR) {
        return <APIError onSubmit={resetToInitialStep} />;
    }

    // Render the appropriate step component based on the current step
    switch (step) {
        case ENROLLMENT_STEPS_NUMBER.ELIGIBILITY_CHECK:
            return (
                <EligibilityCheck
                    formData={formData.eligibility}
                    onChange={handleFormChange}
                    onSubmit={handleCheckEligibility}
                    isSubmitting={isSubmitting}
                    SPONSOR_ID={SPONSOR_ID}
                />
            );
        case ENROLLMENT_STEPS_NUMBER.ELIGIBILITY_SUCCESS:
            return (
                <EligibilitySuccess
                    onSubmit={handleEligibilitySuccessNext}
                />
            );
        case ENROLLMENT_STEPS_NUMBER.CARE_PATHWAY_TREAT:
            return (
                <CarePathwayTreat
                    options={formData.internalFormData.treatQuestionOption}
                    selected={formData.treat.treatOptions}
                    onChange={handleFormChange}
                    onSubmit={handleTreatNext}
                    isSubmitting={isSubmitting}
                />
            );
        case ENROLLMENT_STEPS_NUMBER.CARE_PATHWAY_PREVENT:
            return (
                <CarePathwayPrevent
                    options={formData.internalFormData.preventQuestionOption}
                    selected={formData.prevent.preventOptions}
                    onChange={handleFormChange}
                    onSubmit={handleInterpretationSubmit}
                    isSubmitting={isSubmitting}
                />
            );
        case ENROLLMENT_STEPS_NUMBER.PROGRAM_RESULT:
            return (
                <ProgramResult
                    program={formData.internalFormData.programId?.toUpperCase()}
                    discountCode={formData.internalFormData.discountCode}
                    onContinue={handleProgramContinue}
                    onNoThanks={resetToInitialStep}
                />
            );
        case ENROLLMENT_STEPS_NUMBER.CREATE_ACCOUNT:
            return (
                <CreateAccount
                    formData={formData.createAccount}
                    program={formData.internalFormData.programId?.toUpperCase()}
                    onChange={handleFormChange}
                    onMigrateUser={handleMigrateUser}
                    onSubmit={handleCreateAccount}
                    isSubmitting={isSubmitting}
                     SPONSOR_ID={SPONSOR_ID}
                />
            );
        case ENROLLMENT_STEPS_NUMBER.SHIPPING_ADDRESS:
            return (
                <ShippingAddress
                    formData={formData.shipping}
                    onChange={(field, value) => handleFormChange("shipping", field, value)}
                    onSubmit={handleAddShippingAddress}
                    onVerifyShippingAddress={handleVerifyShippingAddress}
                    isSubmitting={isSubmitting}
                />
            );
        case ENROLLMENT_STEPS_NUMBER.SUCCESS:
            return <EnrollmentSuccess />;
        default:
            return null;
    }
}
